import Vue from 'vue'
import Vuex from 'vuex'
import patientList from './patient-list';
import patientSummary from './patient-summary';
import error from './error';
import followupSession from './followup-session';
import alerts from './alerts';
import dss from './dss';
import encounter from './encounter';
import medicalHistory from './medical-history';
import newPatient from './new-patient';
import expertTool from './expert-tool';
import questionnaires from './questionnaires';
import scenario from './scenario';
import faq from './faq';
import enabledModules from './enabled-modules';

import user from './user';
import app from "../app";
import * as queries from "./queries";
import "./localstorage";

import {
  loadLocalStorageVariableIntoStore,
  registeredLocalStorageVariables,
  storeLocalStorageVariable
} from "@/helper/localstorage";
import moment from "moment/moment";

/**
 * General vuex store
 *
 * @namespace vuex
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/APP_DASHBOARD/store/index.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

Vue.use(Vuex)

const initialState = {
  authToken: null,
  tokenData: {},
  authRefreshToken: null,
  showLogoutRemainingTime: 0,
  loadingCount: 0,
  currentLanguage: 'en',
  serverInfo: [
    {key : "version", value : "0.0.1"}
  ],
  lastRefresh : null
}

const store = new Vuex.Store({
  state: Object.assign({}, initialState),
  mutations: {
    setAuthToken(state, authToken) {
      state.authToken = authToken
    },
    setLastRefresh(state, lastRefresh) {
      state.lastRefresh = lastRefresh
    },
    setAuthRefreshToken(state, authRefreshToken) {
      state.authRefreshToken = authRefreshToken
    },
    setShowLogoutRemainingTime(state, int) {
      state.showLogoutRemainingTime = int
    },
    setLoadingCount(state, int){
      state.loadingCount = int
    },
    setTokenData(state, tokenData){
      state.tokenData = tokenData
    },
    setServerInfo(state, serverInfo){
      state.serverInfo = serverInfo
    },
    setCurrentLanguage(state, language){
      state.currentLanguage = language
      moment.locale(language);
    },
    buildStoreFromLocalStorage(){
      for (let variableKey in registeredLocalStorageVariables) {
        loadLocalStorageVariableIntoStore(variableKey, store)
      }
    },
    resetState (state) {
      Object.assign(state, initialState)
    },
  },
  getters: {
    getServerInfo: (state) => (key) => {
      if (state.serverInfo){
          for (let i=0; i < state.serverInfo.length; i++){
            if (state.serverInfo[i].key === key)
              return state.serverInfo[i].value;
          }
      }

      return null;
    },
    isTestingEnvironment: (state, getters) => {
      if ([
        'dashboard-dev.coroprevention.eu',
        'dashboard-uat.coroprevention.eu',
        'dashboard-qa.coroprevention.eu',
      ].includes(window.location.hostname)) {
        return true;
      }

      if (window.location.hostname === 'dashboard.coroprevention.eu'){
        return false;
      }

      const serverEnv = getters['getServerInfo']('environment');

      return serverEnv === 'dev' || serverEnv === 'test' || serverEnv === null;
    },
    getCurrentLanguage: (state) => state.currentLanguage
  },
  actions: {
    async loadServerInfo({commit}){
      const { data, errors } = await app.apolloMobileClient.query({
        query: queries.serverInfoQuery,
      });

      if (data && !errors) {
        commit('setServerInfo', data.information);
      }
    },
    async setCurrentLanguageAndReload({commit}, currentLanguage) {
      await app.apolloClient.mutate({
        mutation: queries.updateMyUserLanguage,
        variables: {
          preferredLanguage: currentLanguage
        },
      }).finally(() => {
        commit('setCurrentLanguage', currentLanguage)
        storeLocalStorageVariable('currentLanguage', currentLanguage)
        // location.reload()
      })
    },
    setCurrentLanguageWithoutReload({commit}, currentLanguage) {
      commit('setCurrentLanguage', currentLanguage)
      storeLocalStorageVariable('currentLanguage', currentLanguage)
    },
    setLastRefresh({commit}, lastRefresh) {
      commit('setLastRefresh', lastRefresh)
      storeLocalStorageVariable('lastRefresh', lastRefresh)
    },
    login({commit, dispatch}, params) {
      dispatch('setLastRefresh', new Date())
      commit('setAuthToken', params.authToken)
      commit('setAuthRefreshToken', params.refreshToken)
      commit('setTokenData', params.tokenData)

      storeLocalStorageVariable('lastRefresh', new Date())
      storeLocalStorageVariable('authToken', params.authToken)
      storeLocalStorageVariable('authRefreshToken', params.refreshToken)
      storeLocalStorageVariable('tokenData', params.tokenData)

      // Check if new translations are needed
      app.translationManager.loadLanguages(['en', store.state.currentLanguage]);
      dispatch('loadServerInfo');
    },
    logout({commit}) {
      commit('setAuthToken', null)
      commit('setLastRefresh', null)
      commit('setAuthRefreshToken', null)
      commit('setTokenData', {})

      storeLocalStorageVariable('authToken', null)
      storeLocalStorageVariable('lastRefresh', null)
      storeLocalStorageVariable('authRefreshToken', null)
      storeLocalStorageVariable('tokenData', {})
    },
    resetState({commit}) {
      commit('resetState')
      if (window.localStorage){
        window.localStorage.clear()
      }
    }
  },
  modules: {
    patientList,
    patientSummary,
    error,
    followupSession,
    alerts,
    dss,
    encounter,
    medicalHistory,
    newPatient,
    expertTool,
    questionnaires,
    user,
    scenario,
    faq,
    enabledModules,
  },
});

store.commit('buildStoreFromLocalStorage')

export default store;
